// src/components/ImageUpload.jsx

import React, { useState } from 'react';
import { CLOUDINARY_UPLOAD_URL, cloudinaryConfig, getThumbnailUrl } from '../cloudinary';

function ImageUpload({ onImageUpload, currentImageUrl = '', label = 'Görsel' }) {
  const [previewUrl, setPreviewUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState('');

  const processFile = async (file) => {
    if (!file || !file.type.startsWith('image/')) {
        setError('Lütfen geçerli bir görsel dosyası seçin.');
        return;
    }

    setError('');
    const localPreview = URL.createObjectURL(file);
    setPreviewUrl(localPreview);

    try {
      setIsUploading(true);
      const uploaded = await uploadToCloudinary(file);
      setIsUploading(false);

      if (uploaded) {
        onImageUpload(uploaded);
      }
    } catch (e) {
      setIsUploading(false);
      setError('Yükleme sırasında bir hata oluştu.');
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    processFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    processFile(file);
  };

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', cloudinaryConfig.upload_preset);

    const response = await fetch(CLOUDINARY_UPLOAD_URL, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(text || 'Cloudinary yükleme başarısız');
    }

    const data = await response.json();

    const publicId = data.public_id;
    const secureUrl = data.secure_url;

    return {
      publicId,
      secureUrl,
      originalUrl: secureUrl,
      thumbnailUrl: getThumbnailUrl(publicId),
      bytes: data.bytes,
      format: data.format,
      width: data.width,
      height: data.height,
      createdAt: new Date().toISOString(),
      provider: 'cloudinary',
    };
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    setPreviewUrl('');
    setError('');
    onImageUpload(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {label && <label style={{ fontWeight: '600', color: '#374151', fontSize: '14px' }}>{label}</label>}
      
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => document.getElementById(`file-upload-${label}`).click()}
        style={{
          width: '100%',
          minHeight: '140px',
          borderRadius: '16px',
          border: isDragging ? '2px dashed #06C167' : '2px dashed #e2e8f0',
          background: isDragging ? '#f5f3ff' : '#f8fafc',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          position: 'relative',
          padding: '20px',
          boxSizing: 'border-box'
        }}
      >
        <input
          id={`file-upload-${label}`}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          disabled={isUploading}
          style={{ display: 'none' }}
        />

        {(previewUrl || currentImageUrl) ? (
          <div style={{ position: 'relative', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
            <img
              src={previewUrl || currentImageUrl}
              alt="preview"
              style={{ 
                width: '80px', 
                height: '80px', 
                objectFit: 'cover', 
                borderRadius: '12px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            <div style={{ 
                display: 'flex', 
                gap: '8px',
                background: 'white',
                padding: '6px 12px',
                borderRadius: '20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: '1px solid #e2e8f0'
            }}>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#06C167' }}>Değiştirmek için tıklayın veya sürükleyin</span>
                <button
                    type="button"
                    onClick={handleRemove}
                    style={{ 
                        border: 'none', 
                        background: 'none', 
                        color: '#ef4444', 
                        cursor: 'pointer',
                        padding: 0,
                        fontSize: '12px',
                        fontWeight: '700',
                        marginLeft: '4px'
                    }}
                >
                    Kaldır
                </button>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>📸</div>
            <div style={{ fontWeight: '600', color: '#475569', fontSize: '14px' }}>
              Tıklayın veya görseli buraya sürükleyin
            </div>
            <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
              PNG, JPG, GIF (max. 5MB)
            </div>
          </div>
        )}

        {isUploading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255,255,255,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '16px',
            zIndex: 10
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ 
                    width: '20px', 
                    height: '20px', 
                    border: '3px solid #06C167', 
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                }} />
                <span style={{ color: '#06C167', fontWeight: '600', fontSize: '14px' }}>Yükleniyor...</span>
            </div>
          </div>
        )}
        
        <style>
            {`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            `}
        </style>
      </div>
      {error && <div style={{ color: '#ef4444', fontSize: '12px', fontWeight: '500' }}>{error}</div>}
    </div>
  );
}

export default ImageUpload;



